// Vizuální vrstva UNO: pořadí hráčů nahoře, balíček a vrchní karta uprostřed, ruka dole. Jen dotyk/klik, bez hoveru.
// Pro klávesovou čtečku (NVDA) je skrytá (aria-hidden), ta používá zvukovou vrstvu.
// V režimu „dotyk se čtečkou“ (VoiceOver) jsou prvky tlačítka s popisem a oddíly mají nadpisy.
// Kostra se staví jednou a pak se jen aktualizuje (karty v ruce podle id), aby VoiceOver neztratil místo,
// kde právě je, když hraje soupeř.
import type { GameEvent } from "../../../games/types.ts";
import type { Card, Color } from "../../../games/uno/meta.ts";
import { say } from "../../announce.ts";
import { backSvg, cardSvg } from "./cards-svg.ts";
import { cardName, COLOR_NAME, COLORS, cards, hintText, playable, topText, turnText, type Model } from "./model.ts";

const SHAPE_NAME: Record<Color, string> = { r: "●", y: "▲", g: "■", b: "◆" };

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = "", text = ""): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
}

function heading(text: string): HTMLElement {
  const h = el("h2", "visually-hidden", text);
  return h;
}

export class VisualLayer {
  readonly root: HTMLElement;
  showCursor = false;
  private model: Model;
  private accessible = false;
  private flashTop = false;
  private flashReverse = false;

  // Kostra (vytvoří se jednou).
  private order = el("div", "uno-order");
  private orderChips: HTMLElement[] = [];
  private orderArrows: HTMLElement[] = [];
  private wrapStart = el("span", "uno-order-wrap", "↪");
  private wrapEnd = el("span", "uno-order-wrap", "↩");
  private orderDir = el("span", "uno-order-dir");
  private pile = el("div", "uno-pile");
  private pileLabel = el("div", "uno-pile-label");
  private top = el("div", "uno-top");
  private topKey = "";
  private colorChip = el("div", "uno-color-chip");
  private hintText = el("span");
  private passBtn = el("div", "uno-button", "Nechat a dál");
  private hand = el("div", "uno-hand");
  private handEls = new Map<number, HTMLElement>();
  private picker = el("div", "uno-picker");
  private tiles: HTMLElement[] = [];
  private cancelBtn = el("div", "uno-button", "Zpět");

  constructor(model: Model, board: HTMLElement) {
    this.model = model;
    this.root = el("div", "uno-table");
    this.root.setAttribute("aria-hidden", "true");

    this.order.setAttribute("role", "list");
    const upper = el("div", "uno-upper");
    const center = el("div", "uno-center");
    this.pile.dataset.action = "draw";
    this.pile.setAttribute("role", "button");
    this.pile.innerHTML = backSvg();
    this.pile.appendChild(this.pileLabel);
    this.pileLabel.setAttribute("aria-hidden", "true");
    this.top.setAttribute("role", "img");
    this.colorChip.setAttribute("aria-hidden", "true");
    const topWrap = el("div", "uno-top-wrap");
    topWrap.append(this.top, this.colorChip);
    center.append(this.pile, topWrap);
    const hint = el("div", "uno-hint");
    this.passBtn.dataset.action = "pass";
    this.passBtn.setAttribute("role", "button");
    hint.append(this.hintText, this.passBtn);
    upper.append(center, hint);

    this.picker.hidden = true;
    const grid = el("div", "uno-picker-grid");
    COLORS.forEach((c) => {
      const tile = el("div", `uno-color-tile color-${c}`, `${SHAPE_NAME[c]} ${COLOR_NAME[c]}`);
      tile.dataset.color = c;
      tile.setAttribute("role", "button");
      tile.setAttribute("aria-label", COLOR_NAME[c]);
      this.tiles.push(tile);
      grid.appendChild(tile);
    });
    this.cancelBtn.dataset.action = "cancel";
    this.cancelBtn.setAttribute("role", "button");
    this.picker.append(el("div", "uno-picker-title", "Vyber barvu"), grid, this.cancelBtn);

    this.root.append(heading("Hráči a pořadí"), this.order, heading("Stůl"), upper, heading("Tvoje karty"), this.hand, this.picker);
    board.appendChild(this.root);
    this.root.addEventListener("click", (e) => this.click(e.target as HTMLElement));
  }

  destroy() {
    this.root.remove();
  }

  setAccessible(on: boolean) {
    this.accessible = on;
    if (on) this.root.removeAttribute("aria-hidden");
    else this.root.setAttribute("aria-hidden", "true");
    for (const t of [...this.tiles, this.cancelBtn]) {
      if (on) t.tabIndex = -1;
      else t.removeAttribute("tabindex");
    }
    this.render();
  }

  /** Odmítnuté klepnutí: zatřesení, a kdo nevidí, uslyší proč. */
  private reject(target: HTMLElement, text: string) {
    target.classList.remove("wiggle");
    void target.offsetWidth;
    target.classList.add("wiggle");
    if (this.accessible) say(text);
  }

  private click(target: HTMLElement) {
    const view = this.model.view;
    if (!view || view.over) return;
    const node = target.closest<HTMLElement>("[data-id],[data-action],[data-color]");
    if (!node) return;
    if (node.dataset.color) {
      const pick = this.model.picking;
      this.model.picking = null;
      if (pick) this.model.sendAction({ type: "play", cardId: pick.cardId, color: node.dataset.color });
      return this.render();
    }
    if (node.dataset.action === "cancel") {
      this.model.picking = null;
      this.render();
      if (this.accessible) say("Výběr barvy zrušen.");
      return;
    }
    const me = this.model.me;
    if (view.turn !== me) return this.reject(node, turnText(view, me));
    if (node.dataset.action === "draw") {
      if (view.drawnCardId !== null) return this.reject(node, "Už máš líznuto. Zahraj líznutou kartu, nebo Nechat a dál.");
      return this.model.sendAction({ type: "draw" });
    }
    if (node.dataset.action === "pass") return this.model.sendAction({ type: "pass" });
    const id = Number(node.dataset.id);
    const index = view.hand.findIndex((c) => c.id === id);
    const card = view.hand[index];
    if (!card) return;
    if (!playable(view, card)) {
      const why = view.drawnCardId !== null ? "Teď můžeš zahrát jen líznutou kartu." : `Vrchní karta: ${topText(view)}.`;
      return this.reject(node, `${cardName(card)} teď nejde zahrát. ${why}`);
    }
    this.model.cursor = index;
    if (card.color === null) {
      this.model.picking = { cardId: card.id, index: 0 };
      this.render();
      if (this.accessible) {
        // Fokus na první barvu, ať VoiceOver hned nabízí výběr.
        this.tiles[0].focus();
        say("Vyber barvu.");
      }
      return;
    }
    this.model.sendAction({ type: "play", cardId: card.id });
  }

  onEvents(events: GameEvent[]) {
    if (events.some((e) => e.type === "played")) this.flashTop = true;
    if (events.some((e) => e.type === "reversed")) this.flashReverse = true;
  }

  render() {
    const view = this.model.view;
    if (!view) return;
    const me = this.model.me;
    const myTurn = view.turn === me && !view.over;
    this.renderOrder();

    // Balíček a vrchní karta
    const toDraw = view.pendingDraw > 0 && myTurn ? view.pendingDraw : 0;
    this.pileLabel.textContent = toDraw ? `Líznout ${toDraw}` : "Líznout";
    this.pile.setAttribute("aria-label", toDraw ? `Líznout ${cards(toDraw)}` : "Líznout kartu");
    const topKey = `${view.top.id}`;
    if (topKey !== this.topKey) {
      this.top.innerHTML = cardSvg(view.top);
      this.topKey = topKey;
    }
    this.top.classList.toggle("pop", this.flashTop);
    this.flashTop = false;
    this.top.setAttribute("aria-label", `Vrchní karta: ${topText(view)}`);
    this.colorChip.className = `uno-color-chip color-${view.color}`;
    this.colorChip.textContent = `${SHAPE_NAME[view.color]} ${COLOR_NAME[view.color]}`;

    // Nápověda k tahu
    const text = hintText(view, me).replace(/ Enterem.*$/, "").replace("klávesou L", "").replace(", nebo L pro líznutí", ", nebo si lízni");
    this.hintText.textContent = myTurn ? (view.drawnCardId !== null ? "Líznutou kartu můžeš zahrát, nebo si ji nech." : text.replace("stiskni L", "klepni na balíček")) : "";
    this.passBtn.hidden = !(myTurn && view.drawnCardId !== null);

    this.renderHand();

    // Výběr barvy
    const pick = this.model.picking;
    this.picker.hidden = !pick;
    this.tiles.forEach((t, i) => t.classList.toggle("is-cursor", !!pick && this.showCursor && i === pick.index));
    this.root.classList.toggle("my-turn", myTurn);
  }

  /** Pořadí u stolu: místa stálá, šipky podle směru hry, na tahu a další zvýrazněné. */
  private renderOrder() {
    const view = this.model.view!;
    const me = this.model.me;
    const n = view.players.length;
    if (this.orderChips.length !== n) {
      this.order.textContent = "";
      this.orderChips = [];
      this.orderArrows = [];
      this.order.appendChild(this.wrapStart);
      view.players.forEach((_, i) => {
        if (i > 0) {
          const a = el("span", "uno-order-arrow");
          a.setAttribute("aria-hidden", "true");
          this.orderArrows.push(a);
          this.order.appendChild(a);
        }
        const chip = el("span", "uno-order-chip");
        chip.setAttribute("role", "listitem");
        chip.innerHTML = `<small></small><b></b><span class="uno-order-count"></span><span class="uno-fan"></span>`;
        for (const part of chip.children) part.setAttribute("aria-hidden", "true");
        this.orderChips.push(chip);
        this.order.appendChild(chip);
      });
      this.order.append(this.wrapEnd, this.orderDir);
      for (const m of [this.wrapStart, this.wrapEnd]) m.setAttribute("aria-hidden", "true");
    }
    const current = view.players.findIndex((p) => p.id === view.turn);
    const next = (((current + view.direction) % n) + n) % n;
    const arrowChar = n === 2 ? "⇄" : view.direction === 1 ? "→" : "←";
    for (const a of this.orderArrows) a.textContent = arrowChar;
    this.wrapStart.hidden = !(n > 2 && view.direction === -1);
    this.wrapEnd.hidden = !(n > 2 && view.direction === 1);
    const dirText = view.direction === 1 ? "po směru" : "obráceně";
    this.orderDir.hidden = n <= 2;
    this.orderDir.textContent = this.flashReverse ? "Směr se otočil!" : dirText;
    this.order.classList.toggle("flash-reverse", this.flashReverse);
    this.order.setAttribute("aria-label", n > 2 ? `Pořadí hráčů, ${dirText}` : "Pořadí hráčů");
    this.flashReverse = false;

    view.players.forEach((p, i) => {
      const chip = this.orderChips[i];
      const isTurn = i === current && !view.over;
      const isNext = i === next && !view.over && n > 1;
      chip.className = "uno-order-chip" + (isTurn ? " is-turn" : "") + (isNext ? " is-next" : "") + (p.id === me ? " is-me" : "");
      const label = isTurn ? "na tahu" : isNext ? "další" : "";
      const who = p.id === me ? "Ty" : p.name;
      chip.querySelector("small")!.textContent = label || " ";
      chip.querySelector("b")!.textContent = who;
      chip.querySelector(".uno-order-count")!.textContent = cards(p.cards);
      chip.setAttribute("aria-label", `${label ? `${label}: ` : ""}${who}, ${cards(p.cards)}`);
      const fan = chip.querySelector(".uno-fan")!;
      const want = Math.min(p.cards, 10);
      while (fan.children.length < want) {
        const mini = el("span", "uno-mini");
        mini.innerHTML = backSvg();
        fan.appendChild(mini);
      }
      while (fan.children.length > want) fan.lastElementChild!.remove();
    });
  }

  /** Ruka: karty podle id; existující prvky zůstávají (fokus VoiceOveru se neztratí). */
  private renderHand() {
    const view = this.model.view!;
    const myTurn = view.turn === this.model.me && !view.over;
    const ids = new Set(view.hand.map((c) => c.id));
    for (const [id, node] of this.handEls) {
      if (!ids.has(id)) {
        node.remove();
        this.handEls.delete(id);
      }
    }
    let ref = this.hand.firstElementChild;
    view.hand.forEach((c: Card, i) => {
      let node = this.handEls.get(c.id);
      if (!node) {
        node = el("div", "uno-card");
        node.dataset.id = String(c.id);
        node.setAttribute("role", "button");
        node.innerHTML = cardSvg(c);
        this.handEls.set(c.id, node);
      }
      if (node !== ref) this.hand.insertBefore(node, ref);
      else ref = ref.nextElementSibling;
      const canPlay = myTurn && playable(view, c);
      node.classList.toggle("is-playable", canPlay);
      node.classList.toggle("is-drawn", c.id === view.drawnCardId);
      node.classList.toggle("is-cursor", this.showCursor && i === this.model.cursor);
      node.setAttribute("aria-label", `${cardName(c)}${canPlay ? ", hratelná" : ""}${c.id === view.drawnCardId ? ", líznutá" : ""}`);
    });
  }
}
